import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Container, Content } from 'rsuite';

import {
  AppAnalytic,
  AppErrorBoundary,
  AppFooter,
  AppNavbar,
  AppSidebar,
  PrivateRoute,
  PublicRoute,
  ScrollToTop,
  PushNotification,
} from '@common/components';
import {
  CandidateHomeScreen,
  CandidateLoginScreen,
  CandidateProfileScreen,
  CandidateBasicInfoScreen,
  CandidateSignUpScreen,
  CandidateDashboardScreen,
  CandidateJobCategoriesScreen,
  CVMakerHomeScreen,
  CVMakerBuilderScreen,
  ForgotPasswordScreen,
  ResendPasswordScreen,
  HiringPartnerHomeScreen,
  NotFoundScreen,
  PrivacyPolicyScreen,
  TermsConditionScreen,
  ChangePasswordScreen,
  CandidateSearchJobsScreen,
  CandidateSearchCompaniesScreen,
  CandidateJobDetailsScreen,
  CandidateMyCVMakerScreen,
  CandidateCompanyDetailsScreen,
  CandidateMyApplicationScreen,
  CandidateMyApplicationDetailScreen,
  EmailVerifyScreen,
  HiringPartnerLoginScreen,
  HiringPartnerForgotPasswordScreen,
  HiringPartnerResendPasswordScreen,
  HiringPartnerChangePasswordScreen,
  HiringPartnerSignUpScreen,
  HiringPartnerDashboardScreen,
  HiringPartnerMyCompanyScreen,
  HiringPartnerApplicationDetailScreen,
  HiringPartnerJobDetails,
  GeneralTestScreen,
  GeneralTestResultScreen,
  GeneralTestApplyScreen,
  CVMakerPaymentScreen,
} from '@screens';
import ReactGA from 'react-ga4';

// css
import 'rsuite/dist/rsuite.min.css';
import './App.css';
import './main.css';
import './tailwind.css';
import React from 'react';
import { usePathnameListener } from '../hooks/usePathnameListener';

ReactGA.initialize('G-GD6VNJB2YD', {
  testMode: process.env.NODE_ENV === 'development',
});

function App(): JSX.Element {
  const pathname = usePathnameListener();
  const sectionClass = React.useMemo(() => {
    let subPathClassName = '';

    const isHP =
      pathname !== '/hiring-partner' && pathname.includes('/hiring-partner');
    if (isHP) {
      subPathClassName += '-hp';
    }

    const isCVMakerPath = pathname.includes('/cv-maker');
    if (isCVMakerPath) {
      subPathClassName += '-cvmaker';

      if (pathname.split('/').length > 2) {
        subPathClassName += '-white';
      }
    }

    // The candidate auth pages paint their own full-bleed background and
    // their navbar is overlaid, so they must not be offset by the navbar
    // height.
    if (
      pathname === '/login' ||
      pathname === '/forgot-password' ||
      pathname === '/sign-up'
    ) {
      subPathClassName += ' mt-0';
    }

    return subPathClassName;
  }, [pathname]);

  return (
    <AppAnalytic>
      <AppErrorBoundary>
        <BrowserRouter>
          <PushNotification />
          <ScrollToTop />
          <AppSidebar />
          <Container
            className={`app${sectionClass}${
              pathname === '/job-categories' ? ' job-categories-page' : ''
            }`}
          >
            <AppNavbar />

            <Content>
              <Routes>
                <Route element={<PublicRoute />}>
                  {/* Auth */}
                  <Route
                    path="/login/hiring-partner"
                    element={<HiringPartnerLoginScreen />}
                  />
                  <Route path="/login" element={<CandidateLoginScreen />} />
                  <Route
                    path="/forgot-password/hiring-partner"
                    element={<HiringPartnerForgotPasswordScreen />}
                  />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordScreen />}
                  />
                  <Route
                    path="/email-verification"
                    element={<EmailVerifyScreen />}
                  />
                  <Route
                    path="/resend-password-link/hiring-partner"
                    element={<HiringPartnerResendPasswordScreen />}
                  />
                  <Route
                    path="/resend-password-link"
                    element={<ResendPasswordScreen />}
                  />
                  <Route
                    path="/change-password/hiring-partner"
                    element={<HiringPartnerChangePasswordScreen />}
                  />
                  <Route
                    path="/change-password"
                    element={<ChangePasswordScreen />}
                  />
                  <Route
                    path="/sign-up/hiring-partner"
                    element={<HiringPartnerSignUpScreen />}
                  />
                  <Route path="/sign-up" element={<CandidateSignUpScreen />} />
                </Route>

                {/* Misc */}
                <Route
                  path="/privacy-policy"
                  element={<PrivacyPolicyScreen />}
                />
                <Route
                  path="/terms-condition"
                  element={<TermsConditionScreen />}
                />

                {/* * * * *
                 * Private Routes
                 * * * * *
                 */}
                <Route element={<PrivateRoute />}>
                  <Route
                    path="/basic-info"
                    element={<CandidateBasicInfoScreen />}
                  />

                  {/* Dashboard (Hiring-Partner) */}
                  <Route
                    path="/dashboard/hiring-partner/home"
                    element={<HiringPartnerDashboardScreen />}
                  />
                  <Route
                    path="/dashboard/hiring-partner/application/:id"
                    element={<HiringPartnerApplicationDetailScreen />}
                  />
                  <Route
                    path="/dashboard/hiring-partner/mycompany/job/:id"
                    element={<HiringPartnerJobDetails />}
                  />
                  <Route
                    path="/dashboard/hiring-partner/mycompany"
                    element={<HiringPartnerMyCompanyScreen />}
                  />

                  {/* Dashboard (Candidate) */}
                  <Route
                    path="/dashboard/candidate/home"
                    element={<CandidateDashboardScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/profile"
                    element={<CandidateProfileScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/search-jobs"
                    element={<CandidateSearchJobsScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/jobs/:id"
                    element={<CandidateJobDetailsScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/search-companies"
                    element={<CandidateSearchCompaniesScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/my-cv-maker"
                    element={<CandidateMyCVMakerScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/companies/:id"
                    element={<CandidateCompanyDetailsScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/my-application"
                    element={<CandidateMyApplicationScreen />}
                  />
                  <Route
                    path="/dashboard/candidate/my-application/:id"
                    element={<CandidateMyApplicationDetailScreen />}
                  />
                  <Route
                    path="/dashboard/general-test/:token"
                    element={<GeneralTestScreen />}
                  />
                  <Route
                    path="/dashboard/general-test/:token/result"
                    element={<GeneralTestResultScreen />}
                  />
                  <Route
                    path="/dashboard/general-test/:token/apply"
                    element={<GeneralTestApplyScreen />}
                  />

                  {/* CV Maker pages */}
                  <Route
                    path="/cv-maker/build"
                    element={<CVMakerBuilderScreen />}
                  />
                  <Route
                    path="/cv-maker/payment"
                    element={<CVMakerPaymentScreen />}
                  />
                </Route>

                {/* * * * *
                 * Public Routes
                 * * * * *
                 */}
                <Route
                  index
                  path="/"
                  element={<Navigate replace to="/candidate" />}
                />
                <Route path="/candidate" element={<CandidateHomeScreen />} />
                <Route
                  path="/job-categories"
                  element={<CandidateJobCategoriesScreen />}
                />
                <Route path="/cv-maker" element={<CVMakerHomeScreen />} />

                {/* Hiring Partner routes */}
                <Route
                  path="/hiring-partner"
                  element={<HiringPartnerHomeScreen />}
                />

                <Route path="*" element={<NotFoundScreen />} />
              </Routes>
            </Content>

            <AppFooter />
          </Container>
        </BrowserRouter>
      </AppErrorBoundary>
    </AppAnalytic>
  );
}

export default App;
